import { assertNever } from 'circumspect';
import { parsePath } from 'history';
import { preloadLazyComponent } from 'suspendable';
import { PreloadContentOption, PreloadedRoute, Route, Router, RouterEntry, RouterOptions } from '../../types';
import { createHistory } from '../createHistory';
import { getRouterOptionsWithDefaults } from '../getRouterOptionsWithDefaults';
import { matchRoutes } from '../matchRoutes';
import { preloadRoutes } from '../preloadRoutes';

/**
 * Creates a router with the specified routes. The main properties in the
 * definition of each route are the path, the function to preload the data for
 * the route, and the component to render for the route. The created `Router`
 * object can then be passed to the `<PreRouter>` component.
 * @param routes is an array with the definition of all routes.
 * @param options are the router options.
 */
export const createRouter = (routes: Route[], options: RouterOptions = {}): Router => {
  const optionsWithDefaults = getRouterOptionsWithDefaults(options);
  const history = createHistory(optionsWithDefaults.history);
  const preloadedRoutesBeforeNavigation = new Map<string, PreloadedRoute[]>();
  const subscribers = new Set<(routerEntry: RouterEntry) => void>();
  let nextRouteTransitionEnabled = true;

  let currentRouterEntry: RouterEntry = {
    location: history.location,
    preloadedRoutes: preloadRoutes(routes, history.location.pathname, history.location.search),
  };

  const removeHistoryListener = history.listen(({ location }) => {
    if (location.pathname === currentRouterEntry.location.pathname) {
      return;
    }

    const preloadedRoutes = preloadedRoutesBeforeNavigation.get(location.pathname);

    if (preloadedRoutes) {
      currentRouterEntry = {
        location,
        preloadedRoutes,
      };

      preloadedRoutesBeforeNavigation.delete(location.pathname);
    } else {
      currentRouterEntry = {
        location,
        preloadedRoutes: preloadRoutes(routes, location.pathname, location.search),
      };
    }

    subscribers.forEach(subscriber => subscriber(currentRouterEntry));
  });

  const getCurrentRouterEntry = () => currentRouterEntry;

  const refreshCurrentRouterEntry = () => {
    const { location } = currentRouterEntry;

    currentRouterEntry = {
      location,
      preloadedRoutes: preloadRoutes(routes, location.pathname, location.search),
    };

    subscribers.forEach(subscriber => subscriber(currentRouterEntry));
  };

  const preloadComponentsBeforeNavigation = (pathname: string) => {
    const matchedRoutes = matchRoutes(routes, pathname);

    matchedRoutes.forEach(({ route }) => preloadLazyComponent(route.component));
  };

  const preloadRoutesBeforeNavigation = (pathname: string, search?: string) => {
    // Avoid preloading multiple times. The first check is necessary so that if
    // we try to preload multiple times the same route before navigating to it,
    // we only prelaod it once (e.g., link is hovered many times). The second
    // check is necessary so that once we update the router entry and are in the
    // middle of a pending route transition, we don't try to preload the route
    // we are going to again (e.g., link is hovered or pressed during the
    // pending transition).

    // Add in search here
    const fullRoute = (pathname ?? '') + (search ?? '');

    if (
      preloadedRoutesBeforeNavigation.has(fullRoute) ||
      fullRoute === history.location.pathname + history.location.search
    ) {
      return;
    }

    const preloadedRoutes = preloadRoutes(routes, fullRoute, '');

    preloadedRoutesBeforeNavigation.set(fullRoute, preloadedRoutes);
  };

  const preloadBeforeNavigation = (path: string, content: PreloadContentOption) => {
    const { pathname, search } = parsePath(path);

    if (!pathname) {
      return;
    }

    switch (content) {
      case 'code':
        preloadComponentsBeforeNavigation(pathname);
        break;
      case 'code-and-data':
        preloadRoutesBeforeNavigation(pathname, search);
        break;
      case 'none':
        break;
      default:
        assertNever(content);
    }
  };

  const subscribe = (subscriber: (routerEntry: RouterEntry) => void) => {
    subscribers.add(subscriber);

    return () => {
      subscribers.delete(subscriber);
    };
  };

  const isNextRouteTransitionEnabled = () => nextRouteTransitionEnabled;

  const enableNextRouteTransition = () => {
    nextRouteTransitionEnabled = true;
  };

  const disableNextRouteTransition = () => {
    nextRouteTransitionEnabled = false;
  };

  return {
    disableNextRouteTransition,
    enableNextRouteTransition,
    getCurrentRouterEntry,
    history,
    isNextRouteTransitionEnabled,
    options: optionsWithDefaults,
    preloadBeforeNavigation,
    refreshCurrentRouterEntry,
    removeHistoryListener,
    subscribe,
  };
};
